import React, { useState } from "react";
import { useBillingProfileGate, type GateState } from "./billingProfileGate";
import { BillingProfileGateBanner } from "./BillingProfileGateBanner";
import { SetUpBillingProfileDrawer } from "./SetUpBillingProfileDrawer";

export interface BillingGateSurface {
  state: GateState;
  /** True only for Enterprise/Agency with no profile — the payment wall. */
  blocked: boolean;
  /** Renders the gate banner (null when the state is 'none'), wired to open the
   *  Set-up drawer. `surface` scopes the cypress ids so co-rendered banners stay
   *  unique. */
  renderBanner: (surface: string, options?: { variant?: "builder" | "checkout" }) => React.ReactNode;
  /** The inline Set-up drawer; render once per surface. */
  drawer: React.ReactNode;
  openSetup: () => void;
}

/**
 * One-call billing-profile gate surface: a ready banner + the inline Set-up
 * drawer + the derived state, so every Campaign Builder surface (builder shell,
 * order-details drawer, payment drawer, legacy EDDM checkout) wires the gate
 * identically. On profile creation the gate re-derives and the banner clears.
 */
export function useBillingGateSurface(clientId: string | undefined): BillingGateSurface {
  const gate = useBillingProfileGate(clientId);
  const [open, setOpen] = useState(false);

  const renderBanner = (surface: string, options?: { variant?: "builder" | "checkout" }): React.ReactNode =>
    gate.state === "none" ? null : (
      <BillingProfileGateBanner
        surface={surface}
        state={gate.state}
        canSetup={gate.canSetup}
        onSetup={() => setOpen(true)}
        variant={options?.variant ?? "builder"}
      />
    );

  const drawer = (
    <SetUpBillingProfileDrawer
      open={open}
      onClose={() => setOpen(false)}
      onCreated={() => {
        setOpen(false);
        gate.refresh();
      }}
    />
  );

  return {
    state: gate.state,
    blocked: gate.state === "required",
    renderBanner,
    drawer,
    openSetup: () => setOpen(true),
  };
}
